import React, { ReactNode } from "react";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

import {
  DataTableColumn,
  EMPTY_VALUE,
  EntityCell,
  HiddenLabel,
  TextCell,
  Tone,
  formatPersonName,
  statusBadge,
} from "Components/UI/DataTable";
import { ICurrentVehicle } from "Shared/Types/vehicle";

export type VehicleRow = ICurrentVehicle & { is_archived?: boolean };

// `yes` is a legacy API value (see DESIGN.md "Vehicle status").
export const VEHICLE_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "success" },
  inactive: { label: "Inactive", tone: "neutral" },
  in_shop: { label: "In shop", tone: "warning" },
  out_of_service: { label: "Out of service", tone: "danger" },
  downtime: { label: "Downtime", tone: "warning" },
  sold: { label: "Sold", tone: "neutral" },
  yes: { label: "Legacy status", tone: "neutral" },
};

export const vehicleSubtitle = (vehicle: VehicleRow) =>
  [vehicle.year, vehicle.make?.name, vehicle.model?.name]
    .filter(Boolean)
    .join(" ");

export const getVehicleColumns = ({
  renderActions,
}: {
  renderActions: (row: VehicleRow) => ReactNode;
}): DataTableColumn<VehicleRow>[] => [
  {
    id: "name",
    header: "Vehicle",
    meta: { truncate: true },
    cell: ({ row }) => (
      <EntityCell
        icon={<LocalShippingOutlinedIcon />}
        image={row.original.image?.file}
        title={row.original.name || EMPTY_VALUE}
        subtitle={vehicleSubtitle(row.original) || undefined}
      />
    ),
  },
  {
    id: "status",
    header: "Status",
    meta: { width: 150 },
    cell: ({ row }) => statusBadge(VEHICLE_STATUS, row.original.status),
  },
  {
    id: "vin_sn",
    header: "VIN / SN",
    meta: { width: 190 },
    cell: ({ row }) => <TextCell primary={row.original.vin_sn || EMPTY_VALUE} />,
  },
  {
    id: "license_plate",
    header: "License plate",
    meta: { width: 130 },
    cell: ({ row }) => row.original.license_plate || EMPTY_VALUE,
  },
  {
    id: "group",
    header: "Group",
    meta: { width: 150 },
    cell: ({ row }) => row.original.group?.name || EMPTY_VALUE,
  },
  {
    id: "operator",
    header: "Operator",
    meta: { width: 160 },
    cell: ({ row }) => formatPersonName(row.original.operator) || EMPTY_VALUE,
  },
  {
    id: "actions",
    header: () => <HiddenLabel>Actions</HiddenLabel>,
    meta: { width: 56, align: "right", interactive: true },
    cell: ({ row }) => renderActions(row.original),
  },
];
